import { ChangeEvent, ReactElement, useContext } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Select from "react-select"
import { FormLabel, TextInput } from "@wfp/ui"

import { faFileLines, faPlus } from "@fortawesome/free-solid-svg-icons"

import { DataUploadContext } from "../../../../providers/DataUploadContext"
import { useGetMSQuery } from "../../../../services/marketprices/mappingSchemas"

import { ReportStatuses } from "../../../../constants/prices"
import { MappingSchemaData } from "../../../../interfaces/dataInterfaces"

const MappingSettings = (): ReactElement => {
  const { filters, setFilters, resetNewMapping } = useContext(DataUploadContext)

  const { data: mappingSchemas, isFetching: isLoadingMappingSchemas } = useGetMSQuery(
    { adm0code: filters.adm0Code, skip: 0, top: 0, statusId: ReportStatuses.Approved },
    { skip: !filters.adm0Code },
  )

  const handleMappingChange = (item: MappingSchemaData | null): void => {
    setFilters({
      ...filters,
      mappingSchemaId: item?.mappingSchemaId ?? 0,
      mappingSchemaName: "",
    })
    resetNewMapping()
  }

  const handleCreateNewMapping = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilters({ ...filters, mappingSchemaId: 0, mappingSchemaName: event.target.value })
  }

  return (
    <>
      <p className="text-sm text-gray-500">
        Choose an existing mapping schema or create a new one to align your Excel columns with system fields.
      </p>

      <div className="grid grid-cols-2 gap-x-20 items-center" style={{ height: "calc(100vh - 400px)" }}>
        <div
          className={`p-10 border rounded-lg flex flex-col transition-all duration-200 min-h-[350px] ${
            filters.mappingSchemaId
              ? "border-[var(--interactive-01)] border-solid bg-slate-50 border-2"
              : "border-dashed border-gray-300"
          }`}
        >
          <FontAwesomeIcon
            icon={faFileLines}
            size="3x"
            color={filters.mappingSchemaId ? "var(--interactive-01)" : "#ccc"}
          />
          <div className="mt-5">
            <FormLabel htmlFor="mapping">Use an Existing Mapping Schema</FormLabel>
            <p className="text-sm text-gray-500 mb-2">Choose a saved mapping schema to apply to your Excel file.</p>
            <Select
              inputId="mapping"
              isLoading={isLoadingMappingSchemas}
              placeholder="Select a saved Mapping Schema"
              options={mappingSchemas?.items ?? []}
              isClearable
              isSearchable
              classNamePrefix="select"
              menuPosition="fixed"
              onChange={handleMappingChange}
              getOptionLabel={option => option.mappingSchemaName}
              getOptionValue={option => option.mappingSchemaId.toString()}
              value={mappingSchemas?.items?.find(item => item.mappingSchemaId === filters.mappingSchemaId) ?? null}
            />
          </div>
        </div>

        <div
          className={`p-10 border rounded-lg flex flex-col transition-all duration-200 min-h-[350px] ${
            filters.mappingSchemaName
              ? "border-[var(--interactive-01)] border-solid bg-slate-50 border-2"
              : "border-dashed border-gray-300"
          }`}
        >
          <FontAwesomeIcon
            icon={faPlus}
            size="3x"
            color={filters.mappingSchemaName ? "var(--interactive-01)" : "#ccc"}
          />
          <div className="mt-5 mb-3">
            <FormLabel htmlFor="new-mapping">Create a new Mapping Schema</FormLabel>
            <p className="text-sm text-gray-500 mb-2">Name your mapping schema and define column matches.</p>
            <TextInput
              id="new-mapping"
              labelText=""
              name="name"
              placeholder="Enter a mapping schema name"
              type="text"
              className="custom-text-field"
              onChange={handleCreateNewMapping}
              value={filters.mappingSchemaName}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default MappingSettings
